import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth'
import { Crown, Palette } from 'lucide-react'
import { auth, db } from '../firebase'
import { doc, setDoc } from '../security/SecureFirebase.js'
import TenantService from '../services/firebase/tenants'

export default function SignupWhiteLabel() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '', businessName: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (event) => {
    const { name, value } = event.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setError('')
    setIsSubmitting(true)

    try {
      if (formData.password !== formData.confirmPassword) {
        throw new Error('Passwords do not match')
      }

      const credential = await createUserWithEmailAndPassword(auth, formData.email, formData.password)
      const createdUser = credential.user

      await updateProfile(createdUser, {
        displayName: formData.businessName
      })

      await createdUser.getIdToken(true)

      const tenantResult = await TenantService.createTenantForUser(createdUser, 'Agency', {
        billingCycle: 'yearly',
        planType: 'whitelabel_yearly',
        businessName: formData.businessName,
        hasWhiteLabel: true,
        status: 'Active'
      })

      if (tenantResult.error) {
        throw new Error(tenantResult.error.message || 'Failed to create your agency workspace')
      }

      await setDoc(doc(db, 'MarketGenie_tenants', createdUser.uid), {
        plan: 'Agency',
        planType: 'whitelabel_yearly',
        status: 'Active',
        hasWhiteLabel: true,
        settings: {
          companyName: formData.businessName
        }
      }, { merge: true })

      navigate('/whitelabel-setup', { replace: true })
    } catch (err) {
      console.error('White label signup error:', err)
      setError(err.message || 'Unable to create your partner account. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-yellow-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-amber-200 overflow-hidden">
        <div className="bg-gradient-to-r from-amber-500 to-yellow-400 text-white p-6">
          <div className="flex items-center gap-2 mb-2">
            <Crown className="h-7 w-7" />
            <span className="text-sm uppercase tracking-[0.2em] font-semibold">Platform Partner</span>
          </div>
          <h1 className="text-3xl font-bold">Welcome, Platform Partner!</h1>
          <p className="text-sm text-white/90 mt-2">Create your agency account and head straight to your white-label branding setup.</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 flex items-center gap-2">
            <Palette className="h-4 w-4" />
            Premium white-label setup with branding access included.
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
            <input
              type="text"
              name="businessName"
              required
              value={formData.businessName}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber-500"
              placeholder="Your Agency Name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber-500"
              placeholder="you@agency.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              name="password"
              required
              minLength={6}
              value={formData.password}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber-500"
              placeholder="Create a secure password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <input
              type="password"
              name="confirmPassword"
              required
              minLength={6}
              value={formData.confirmPassword}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber-500"
              placeholder="Confirm your password"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-lg bg-gradient-to-r from-amber-500 to-yellow-400 text-white py-3 font-semibold hover:from-amber-600 hover:to-yellow-500 disabled:opacity-60"
          >
            {isSubmitting ? 'Creating Account...' : 'Create Partner Account'}
          </button>
        </form>
      </div>
    </div>
  )
}
